import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Pause,
  Play,
  XCircle,
  Download,
  Pencil,
  AlertTriangle,
  Clock,
  CheckCircle,
  Search,
  Filter,
  BarChart3,
  Layout,
  X,
  FilePlus,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import axios from 'axios';
import ContractWizard from './ContractWizard';
import ContractForm from './ContractForm';
import ContractAnalytics from './ContractAnalytics';
import ContractTemplateBuilder from './ContractTemplateBuilder';

// Amendment Modal Component
const AmendmentModal = ({ contract, amendment, onClose, onSave, user }) => {
  const isEditing = !!amendment;

  const [formData, setFormData] = useState({
    amendment_number: '',
    effective_date: new Date().toISOString().split('T')[0],
    previous_rate: contract?.current_rate || '',
    new_rate: '',
    previous_rate_travel: contract?.rate_travel || '',
    new_rate_travel: '',
    previous_rate_training: contract?.rate_training || '',
    new_rate_training: '',
    previous_custom_rates: contract?.custom_rates || {},
    new_custom_rates: contract?.custom_rates || {},
    current_end_date: contract?.end_date || '',
    new_end_date: '',
    reason: 'Rate change amendment',
    pdf_document: null,
    template_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [templates, setTemplates] = useState([]);

  const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

  useEffect(() => {
    if (isEditing && amendment) {
      // Load existing amendment data
      setFormData({
        amendment_number: amendment.amendment_number || '',
        effective_date: amendment.effective_date || new Date().toISOString().split('T')[0],
        previous_rate: amendment.previous_rate || '',
        new_rate: amendment.new_rate || '',
        previous_rate_travel: amendment.previous_rate_travel || '',
        new_rate_travel: amendment.new_rate_travel || '',
        previous_rate_training: amendment.previous_rate_training || '',
        new_rate_training: amendment.new_rate_training || '',
        previous_custom_rates: amendment.previous_custom_rates || {},
        new_custom_rates: amendment.new_custom_rates || {},
        current_end_date: amendment.previous_end_date || '',
        new_end_date: amendment.new_end_date || '',
        reason: amendment.reason || '',
        pdf_document: null,
        template_id: ''
      });
    } else {
      // Auto-generate amendment number for new amendment
      const nextNumber = (contract?.amendments?.length || 0) + 1;
      setFormData(prev => ({
        ...prev,
        amendment_number: `Aneks ${nextNumber}`,
        previous_rate: contract?.current_rate || '',
        previous_rate_travel: contract?.rate_travel || '',
        previous_rate_training: contract?.rate_training || '',
        previous_custom_rates: contract?.custom_rates || {},
        new_custom_rates: contract?.custom_rates || {},
        current_end_date: contract?.end_date || ''
      }));
    }

    // Fetch templates
    fetchTemplates();
  }, [contract, amendment, isEditing]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/contract-templates/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      setTemplates(response.data);
      // Set default template if available
      const defaultTemplate = response.data.find(t => t.is_default);
      if (defaultTemplate) {
        setFormData(prev => ({ ...prev, template_id: defaultTemplate.id }));
      } else if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, template_id: response.data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.amendment_number) newErrors.amendment_number = 'Amendment number is required';
    if (!formData.effective_date) newErrors.effective_date = 'Effective date is required';
    if (!formData.new_rate || formData.new_rate <= 0) newErrors.new_rate = 'Valid new rate is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        // Skip template_id as it's not in the model
        if (key === 'template_id') return;

        // Map current_end_date to previous_end_date
        if (key === 'current_end_date') {
          if (formData[key]) {
            submitData.append('previous_end_date', formData[key]);
          }
          return;
        }

        if (key === 'pdf_document') {
          if (formData[key] instanceof File) {
            submitData.append(key, formData[key]);
          }
        } else if (key === 'previous_custom_rates' || key === 'new_custom_rates') {
          // JSON fields need to be stringified
          submitData.append(key, JSON.stringify(formData[key] || {}));
        } else if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      if (isEditing) {
        await axios.put(
          `${API_BASE_URL}/contracts/amendments/${amendment.id}/`,
          submitData,
          {
            headers: {
              Authorization: `Bearer ${user.accessToken}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/contracts/${contract.id}/amendments/`,
          submitData,
          {
            headers: {
              Authorization: `Bearer ${user.accessToken}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }

      onSave();
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} amendment:`, error);
      console.error('Response data:', error.response?.data);
      alert(`Error ${isEditing ? 'updating' : 'creating'} amendment: ${JSON.stringify(error.response?.data)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">{isEditing ? 'Edit Amendment' : 'Add Amendment to Contract'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {/* Header info */}
          <div className="bg-gray-50 p-2 rounded-md flex gap-6 text-xs">
            <p className="text-gray-600">Contract: <span className="font-medium">{contract?.contract_number}</span></p>
            <p className="text-gray-600">User: <span className="font-medium">{contract?.user_name}</span></p>
          </div>

          {/* Basic fields in row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Amendment Number *</label>
              <input
                type="text"
                name="amendment_number"
                value={formData.amendment_number}
                onChange={handleChange}
                className={`w-full px-2 py-1.5 border rounded-md text-sm ${errors.amendment_number ? 'border-red-500' : 'border-gray-300'}`}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Effective Date *</label>
              <input
                type="date"
                name="effective_date"
                value={formData.effective_date}
                onChange={handleChange}
                className={`w-full px-2 py-1.5 border rounded-md text-sm ${errors.effective_date ? 'border-red-500' : 'border-gray-300'}`}
              />
            </div>
          </div>

          {/* All Rates in grid */}
          <div className="border-t pt-3">
            <h4 className="text-xs font-medium text-gray-700 mb-2">Rate Changes</h4>
            <div className="grid grid-cols-4 gap-3">
              {/* Main Rate */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Previous Rate</label>
                <input
                  type="number"
                  name="previous_rate"
                  value={formData.previous_rate}
                  readOnly
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-gray-100 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">New Rate *</label>
                <input
                  type="number"
                  name="new_rate"
                  value={formData.new_rate}
                  onChange={handleChange}
                  step="0.01"
                  className={`w-full px-2 py-1.5 border rounded-md text-sm ${errors.new_rate ? 'border-red-500' : 'border-gray-300'}`}
                />
              </div>

              {/* Rate Travel */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Prev. Travel</label>
                <input
                  type="number"
                  name="previous_rate_travel"
                  value={formData.previous_rate_travel}
                  readOnly
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-gray-100 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">New Travel</label>
                <input
                  type="number"
                  name="new_rate_travel"
                  value={formData.new_rate_travel}
                  onChange={handleChange}
                  step="0.01"
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                />
              </div>

              {/* Rate Training */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Prev. Training</label>
                <input
                  type="number"
                  name="previous_rate_training"
                  value={formData.previous_rate_training}
                  readOnly
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-gray-100 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">New Training</label>
                <input
                  type="number"
                  name="new_rate_training"
                  value={formData.new_rate_training}
                  onChange={handleChange}
                  step="0.01"
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                />
              </div>

              {/* Custom Rates */}
              {Object.entries(formData.previous_custom_rates || {}).map(([name, prevValue]) => (
                <React.Fragment key={name}>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Prev. {name}</label>
                    <input
                      type="number"
                      value={prevValue}
                      readOnly
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-gray-100 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">New {name}</label>
                    <input
                      type="number"
                      value={formData.new_custom_rates[name] || ''}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          new_custom_rates: {
                            ...prev.new_custom_rates,
                            [name]: e.target.value
                          }
                        }));
                      }}
                      step="0.01"
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* End Date, Template, PDF in one row */}
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Current End Date</label>
              <input
                type="date"
                name="current_end_date"
                value={formData.current_end_date}
                readOnly
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-gray-100 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">New End Date</label>
              <input
                type="date"
                name="new_end_date"
                value={formData.new_end_date}
                onChange={handleChange}
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Template</label>
              <select
                name="template_id"
                value={formData.template_id}
                onChange={handleChange}
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
              >
                <option value="">-- Select --</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">PDF (optional)</label>
              <input
                type="file"
                name="pdf_document"
                onChange={handleChange}
                accept=".pdf"
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Changes' : 'Create Amendment')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

const ContractManagement = () => {
  const { user } = useContext(UserContext);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showExpiringSoon, setShowExpiringSoon] = useState(false);
  const [activeTab, setActiveTab] = useState('contracts');
  const [showAmendmentModal, setShowAmendmentModal] = useState(false);
  const [amendmentContract, setAmendmentContract] = useState(null);
  const [editingAmendment, setEditingAmendment] = useState(null);
  const [expandedContract, setExpandedContract] = useState(null);

  useEffect(() => {
    if (user) {
      fetchContracts();
    }
  }, [user, statusFilter, showExpiringSoon]);

  const fetchContracts = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (showExpiringSoon) params.expiring_soon = 'true';

      const response = await axios.get(`${API_BASE_URL}/contracts/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` },
        params
      });
      setContracts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (contractId) => {
    if (!window.confirm('Are you sure you want to delete this contract?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/contracts/${contractId}/`, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      fetchContracts();
    } catch (error) {
      console.error('Error deleting contract:', error);
      alert('Error deleting contract');
    }
  };

  const handleStatusChange = async (contractId, action) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/contracts/${contractId}/${action}/`,
        {},
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );
      fetchContracts();
    } catch (error) {
      console.error(`Error ${action} contract:`, error);
      alert(`Error ${action} contract`);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-blue-100 text-blue-800',
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-yellow-100 text-yellow-800',
      terminated: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'suspended': return <Pause className="w-4 h-4" />;
      case 'terminated': return <XCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const filteredContracts = contracts.filter(contract =>
    contract.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contract.contract_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading contracts...</div>;
  }

  if (showWizard) {
    return (
      <ContractWizard
        contract={editingContract}
        onClose={() => {
          setShowWizard(false);
          setEditingContract(null);
        }}
        onSave={() => {
          setShowWizard(false);
          setEditingContract(null);
          fetchContracts();
        }}
      />
    );
  }

  if (showForm) {
    return (
      <ContractForm
        contract={editingContract}
        onClose={() => {
          setShowForm(false);
          setEditingContract(null);
        }}
        onSave={() => {
          setShowForm(false);
          setEditingContract(null);
          fetchContracts();
        }}
      />
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contract Management</h1>
            <p className="text-sm text-gray-600">
              Manage employee contracts and rates ({contracts.length} total)
            </p>
          </div>
          {activeTab === 'contracts' && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowWizard(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
                Create New Contract
              </button>
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                <Edit className="w-4 h-4" />
                Quick Add
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200 mb-4">
          <button
            onClick={() => setActiveTab('contracts')}
            className={`px-4 py-2 font-medium transition-colors relative ${
              activeTab === 'contracts'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Contracts
            </div>
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 font-medium transition-colors relative ${
              activeTab === 'analytics'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Analytics
            </div>
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 font-medium transition-colors relative ${
              activeTab === 'templates'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Layout className="w-4 h-4" />
              Templates
            </div>
          </button>
        </div>

        {/* Filters - Only show for contracts tab */}
        {activeTab === 'contracts' && (
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by user or contract number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="terminated">Terminated</option>
          </select>
          <button
            onClick={() => setShowExpiringSoon(!showExpiringSoon)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md border ${
              showExpiringSoon
                ? 'bg-orange-50 border-orange-300 text-orange-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <AlertTriangle className="w-4 h-4" />
            Expiring Soon
          </button>
        </div>
        )}
      </div>

      {/* Contracts Table - Only show for contracts tab */}
      {activeTab === 'contracts' && (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contract Number
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Duration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rate
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredContracts.map(contract => (
              <React.Fragment key={contract.id}>
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{contract.user_name}</div>
                  <div className="text-sm text-gray-500">{contract.company_name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{contract.contract_number}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {new Date(contract.start_date).toLocaleDateString()}
                    {' → '}
                    {contract.is_indefinite ? (
                      <span className="text-blue-600 font-medium">Indefinite</span>
                    ) : (
                      <>
                        {new Date(contract.end_date).toLocaleDateString()}
                        {contract.amendments?.filter(a => a.new_end_date).length > 0 && (
                          <span className="ml-1 text-xs text-purple-600">
                            ({contract.amendments.filter(a => a.new_end_date).slice(-1)[0]?.amendment_number})
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {contract.is_expiring_soon && (
                    <div className="flex items-center gap-1 text-xs text-orange-600">
                      <AlertTriangle className="w-3 h-3" />
                      Expiring in {contract.days_until_expiry} days
                    </div>
                  )}
                  {contract.is_expired && (
                    <div className="flex items-center gap-1 text-xs text-red-600">
                      <XCircle className="w-3 h-3" />
                      Expired
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {contract.current_rate} {contract.currency}
                  </div>
                  <div className="text-xs text-gray-500 capitalize">{contract.rate_type}</div>
                  {/* Show additional rates if present */}
                  {(contract.rate_travel || contract.rate_training || (contract.custom_rates && Object.keys(contract.custom_rates).length > 0)) && (
                    <div className="text-xs text-gray-500 mt-1">
                      {contract.rate_travel && <span className="block">Travel: {contract.rate_travel} {contract.currency}</span>}
                      {contract.rate_training && <span className="block">Training: {contract.rate_training} {contract.currency}</span>}
                      {contract.custom_rates && Object.entries(contract.custom_rates).map(([name, value]) => {
                        // Handle both simple values and object values
                        const displayValue = typeof value === 'object' ? (value?.rate || value?.value || JSON.stringify(value)) : value;
                        return displayValue && <span key={name} className="block">{name}: {displayValue} {contract.currency}</span>;
                      })}
                    </div>
                  )}
                  {contract.amendments?.length > 0 && (
                    <button
                      onClick={() => setExpandedContract(expandedContract === contract.id ? null : contract.id)}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                    >
                      {contract.amendments.length} amendment(s)
                      {expandedContract === contract.id ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      )}
                    </button>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(contract.status)}`}>
                    {getStatusIcon(contract.status)}
                    {contract.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setEditingContract(contract);
                        setShowWizard(true);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => {
                        setAmendmentContract(contract);
                        setShowAmendmentModal(true);
                      }}
                      className="text-purple-600 hover:text-purple-800"
                      title="Add Amendment"
                    >
                      <FilePlus className="w-4 h-4" />
                    </button>

                    {contract.status === 'active' && (
                      <button
                        onClick={() => handleStatusChange(contract.id, 'suspend')}
                        className="text-yellow-600 hover:text-yellow-800"
                        title="Suspend"
                      >
                        <Pause className="w-4 h-4" />
                      </button>
                    )}

                    {contract.status === 'suspended' && (
                      <button
                        onClick={() => handleStatusChange(contract.id, 'activate')}
                        className="text-green-600 hover:text-green-800"
                        title="Activate"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                    )}

                    {contract.status !== 'terminated' && (
                      <button
                        onClick={() => {
                          if (window.confirm('Terminate this contract? This cannot be undone.')) {
                            handleStatusChange(contract.id, 'terminate');
                          }
                        }}
                        className="text-red-600 hover:text-red-800"
                        title="Terminate"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}

                    <button
                      onClick={() => {
                        fetch(`${API_BASE_URL}/contracts/${contract.id}/download-template/`, {
                          headers: { Authorization: `Bearer ${user.accessToken}` }
                        })
                          .then(response => {
                            if (!response.ok) {
                              throw new Error('Failed to download PDF');
                            }
                            return response.blob();
                          })
                          .then(blob => {
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `contract_${contract.contract_number}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          })
                          .catch(err => {
                            console.error('Error downloading PDF:', err);
                            alert('Error downloading PDF. Make sure a template exists for this company.');
                          });
                      }}
                      className="text-gray-600 hover:text-gray-800"
                      title="Download PDF"
                    >
                      <Download className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => handleDelete(contract.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
              {/* Expanded amendments row */}
              {expandedContract === contract.id && contract.amendments?.length > 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-4 bg-gray-50">
                    <div className="text-sm font-medium text-gray-700 mb-2">Amendments:</div>
                    <table className="min-w-full divide-y divide-gray-200 border rounded">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Amendment</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Effective Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Rate Change</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">End Date Change</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Created By</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {contract.amendments.map(amendment => (
                          <tr key={amendment.id} className={amendment.is_suspended ? "bg-gray-100 opacity-60" : ""}>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {amendment.amendment_number}
                              {amendment.is_suspended && (
                                <span className="ml-2 text-xs text-yellow-600 font-medium">(Suspended)</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {new Date(amendment.effective_date).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <span className="text-gray-500">{amendment.previous_rate}</span>
                              <span className="mx-1">→</span>
                              <span className="text-gray-900 font-medium">{amendment.new_rate} {contract.currency}</span>
                            </td>
                            <td className="px-4 py-2 text-sm">
                              {amendment.new_end_date ? (
                                <>
                                  <span className="text-gray-500">
                                    {amendment.previous_end_date ? new Date(amendment.previous_end_date).toLocaleDateString() : 'Indefinite'}
                                  </span>
                                  <span className="mx-1">→</span>
                                  <span className="text-green-600 font-medium">
                                    {new Date(amendment.new_end_date).toLocaleDateString()}
                                  </span>
                                </>
                              ) : (
                                <span className="text-gray-400">No change</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              {amendment.created_by_name || '-'}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => {
                                    setEditingAmendment(amendment);
                                    setAmendmentContract(contract);
                                    setShowAmendmentModal(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="Edit Amendment"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    fetch(`${API_BASE_URL}/contracts/amendments/${amendment.id}/download/`, {
                                      headers: { Authorization: `Bearer ${user.accessToken}` }
                                    })
                                      .then(response => {
                                        if (!response.ok) {
                                          throw new Error('Failed to download PDF');
                                        }
                                        return response.blob();
                                      })
                                      .then(blob => {
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `amendment_${amendment.amendment_number}_${contract.contract_number}.pdf`;
                                        document.body.appendChild(a);
                                        a.click();
                                        window.URL.revokeObjectURL(url);
                                        document.body.removeChild(a);
                                      })
                                      .catch(err => {
                                        console.error('Error downloading PDF:', err);
                                        alert('Error downloading amendment PDF. Make sure a template exists.');
                                      });
                                  }}
                                  className="text-gray-600 hover:text-gray-800"
                                  title="Download Amendment PDF"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={async () => {
                                    try {
                                      await axios.patch(
                                        `${API_BASE_URL}/contract-amendments/${amendment.id}/`,
                                        { is_suspended: !amendment.is_suspended },
                                        { headers: { Authorization: `Bearer ${user.accessToken}` } }
                                      );
                                      fetchContracts();
                                    } catch (err) {
                                      console.error('Error toggling suspension:', err);
                                      alert('Error updating amendment status');
                                    }
                                  }}
                                  className={amendment.is_suspended ? "text-green-600 hover:text-green-800" : "text-yellow-600 hover:text-yellow-800"}
                                  title={amendment.is_suspended ? "Activate Amendment" : "Suspend Amendment"}
                                >
                                  {amendment.is_suspended ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {filteredContracts.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No contracts found</p>
            {searchTerm && (
              <p className="text-sm text-gray-400 mt-2">Try adjusting your search or filters</p>
            )}
          </div>
        )}
      </div>
      )}

      {/* Analytics - Only show for analytics tab */}
      {activeTab === 'analytics' && (
        <ContractAnalytics />
      )}

      {/* Templates - Only show for templates tab */}
      {activeTab === 'templates' && (
        <ContractTemplateBuilder />
      )}

      {/* Amendment Modal */}
      {showAmendmentModal && amendmentContract && (
        <AmendmentModal
          contract={amendmentContract}
          amendment={editingAmendment}
          user={user}
          onClose={() => {
            setShowAmendmentModal(false);
            setAmendmentContract(null);
            setEditingAmendment(null);
          }}
          onSave={() => {
            setShowAmendmentModal(false);
            setAmendmentContract(null);
            setEditingAmendment(null);
            fetchContracts();
          }}
        />
      )}
    </div>
  );
};

export default ContractManagement;
